import { useCallback, useEffect, useRef } from 'react'

import { useSessionStorage } from 'usehooks-ts'
import Link from '@/components/link-component'
import Disclosure from '@/components/ui/disclosure'
import { usePage } from '@inertiajs/react'
import { TypeHelpNavFields, TypeHelpNavSkeleton } from '@/types/ctf'
import { useTranslations } from '@/utils/locale'
import { cx } from '@/utils/strings'
// import BwiPlus from '../icons/bwi/plus-square.svg'
// import BwiMinus from '../icons/bwi/minus-square.svg'

type HelpNavProps = {
  helpNav: TypeHelpNavSkeleton[]
  helpNavInitial: any
}
export default function HelpNav({ helpNav, helpNavInitial }: HelpNavProps): JSX.Element {
  const { t } = useTranslations()
  // help remember state between page loads
  const helpNavRef = useRef(null)
  const [indexes, setIndexes] = useSessionStorage('helpNavIndexes', [])
  const [scrollY, setScrollY] = useSessionStorage<number>('helpNavScrollY', 0)
  const HelpLink = useCallback(({ className, children, slug = null }) => {
    const page = usePage()
    const pathname = page.url
    const path = !slug ? '/help/' : `/help/${slug}/`

    return (
      <Link
        to={path}
        className={cx(
          'text-sm xl:text-base text-indigoBlue dark:text-accent-teal px-4 py-1 mb-1 rounded-md leading-tight w-full block hover:bg-secondary-100 dark:hover:bg-secondary-800',
          pathname === path && 'bg-secondary-200 dark:bg-secondary-800',
          className
        )}
        onClick={() => setScrollY(helpNavRef?.current?.scrollTop)}
      >
        {children}
      </Link>
    )
  }, [])

  const disclosureClickHandler = useCallback((d0, d1 = undefined, d2 = undefined) => {
    setIndexes((prev) => {
      if (d1 === undefined) {
        if (!prev[d0]) prev[d0] = { expanded: true, children: [] }
        else prev[d0].expanded = !prev[d0].expanded
      } else if (d2 === undefined) {
        if (!prev[d0].children[d1]) prev[d0].children[d1] = { expanded: true, children: [] }
        else prev[d0].children[d1].expanded = !prev[d0].children[d1].expanded
      } else {
        if (!prev[d0].children[d1].children[d2]) prev[d0].children[d1].children[d2] = { expanded: true }
        else prev[d0].children[d1].children[d2].expanded = !prev[d0].children[d1].children[d2].expanded
      }
      return prev
    })
  }, [])

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      sessionStorage.removeItem('helpNavIndexes')
      sessionStorage.removeItem('helpNavScrollY')
    })
    if (helpNavRef?.current?.scrollTo && scrollY > 0) (helpNavRef.current as HTMLElement).scrollTo(0, scrollY)
  }, [])

  return (
    <nav
      className="text-indigoBlue dark:text-white h-fit overflow-y-auto pb-12 max-h-full lg:max-h-[calc(100vh-10rem)] lg:mb-4"
      ref={helpNavRef}
    >
      <div className="mt-4 lg:mt-0 px-2 xl:px-4">
        <HelpLink className="pl-2">{t('Home')}</HelpLink>
        <HelpLink className="pl-2" slug="releasenotes">
          {t('Release Notes')}
        </HelpLink>
        {helpNav.map((node, d0) => (
          <ul key={`nav-toplnk-${node.sys.id}`} className="list-none mt-2">
            <li>
              <Disclosure
                heading={<span className="font-semibold xl:font-bold text-[15px] xl:text-lg">{node.fields.title}</span>}
                defaultOpen={helpNavInitial?.[0]?.index === d0 || indexes[d0]?.expanded === true}
                onClick={() => disclosureClickHandler(d0)}
              >
                {node.fields?.items &&
                  node.fields.items.map((item, d1) => (
                    <ul key={`nav-sublnk-${d1}`} className="list-none">
                      {item.fields?.slug && (
                        <li>
                          <HelpLink key={item.fields.slug} slug={item.fields.slug}>
                            {item.fields.title}
                          </HelpLink>
                        </li>
                      )}
                      {Array.isArray(item.fields?.items) && (
                        <li>
                          <Disclosure
                            heading={
                              <span className="font-semibold xl:font-bold text-[15px] xl:text-lg ml-2">
                                {item.fields.title}
                              </span>
                            }
                            defaultOpen={
                              (helpNavInitial?.[0]?.index === d0 && helpNavInitial?.[1]?.index === d1) ||
                              indexes[d0]?.children[d1]?.expanded === true
                            }
                            onClick={() => disclosureClickHandler(d0, d1)}
                          >
                            {item.fields.items.map((subItem, d2) => (
                              <ul key={subItem.fields.title} className="list-none ml-2">
                                {subItem.fields.slug && (
                                  <li>
                                    <HelpLink key={subItem.fields.slug} slug={subItem.fields.slug}>
                                      {subItem.fields.title}
                                    </HelpLink>
                                  </li>
                                )}
                                {subItem.fields.items && (
                                  <li>
                                    <Disclosure
                                      heading={
                                        <span className="font-semibold xl:font-bold text-[15px] xl:text-lg ml-2">
                                          {subItem.fields.title}
                                        </span>
                                      }
                                      defaultOpen={
                                        (helpNavInitial?.[0]?.index === d0 &&
                                          helpNavInitial?.[1]?.index === d1 &&
                                          helpNavInitial?.[2]?.index === d2) ||
                                        indexes[d0]?.children[d1]?.children[d2]?.expanded === true
                                      }
                                      onClick={() => disclosureClickHandler(d0, d1, d2)}
                                    >
                                      {subItem.fields.items.map((subSubItem) => (
                                        <ul key={subSubItem.fields.title} className="list-none ml-2">
                                          {subSubItem.fields.slug && (
                                            <li>
                                              <HelpLink key={subSubItem.fields.slug} slug={subSubItem.fields.slug}>
                                                {subSubItem.fields.title}
                                              </HelpLink>
                                            </li>
                                          )}
                                        </ul>
                                      ))}
                                    </Disclosure>
                                  </li>
                                )}
                              </ul>
                            ))}
                          </Disclosure>
                        </li>
                      )}
                    </ul>
                  ))}
              </Disclosure>
            </li>
          </ul>
        ))}
      </div>
    </nav>
  )
}
